import React, { useState, useEffect } from 'react';
import { X, Clock, CheckCircle2, ShieldCheck, ArrowRight, Loader2, Phone } from 'lucide-react';
import { servicesData } from '../../data/servicesData';
import { companyInfo } from '../../data/companyInfo';
import { Logo } from './Logo';
import { sendConsultationRequest } from '../../services/emailService';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultService?: string;
}

export const ConsultationModal: React.FC<ConsultationModalProps> = ({ isOpen, onClose, defaultService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: defaultService || servicesData[0].title,
    preferredTime: 'Morning (10:00 AM - 1:00 PM IST)',
    budget: '$5,000 - $15,000',
    notes: '',
    ndaConsent: true,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (defaultService) {
      setFormData(prev => ({ ...prev, service: defaultService }));
    }
  }, [defaultService]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError('Please fill in your name, business email, and phone number.');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const res = await sendConsultationRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        preferredTime: formData.preferredTime,
        budget: formData.budget,
        notes: formData.notes,
      });

      setLoading(false);
      if (res.success) {
        setSubmitted(true);
      } else {
        setError(res.error || 'Failed to dispatch request. Please email info@ebonmedia.com');
      }
    } catch (err: any) {
      setLoading(false);
      setError('Connection error. Please contact info@ebonmedia.com directly.');
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050A13]/80 backdrop-blur-sm animate-fade-in" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-2xl bg-[#071426] border border-[#1b2f4a] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-[#1b2f4a] flex items-center justify-between bg-[#0B2342]/40">
          <div className="flex items-center gap-3">
            <Logo variant="card" className="scale-90 origin-left hidden sm:inline-flex" />
            <div>
              <span className="text-xs uppercase tracking-wider text-[#FF851B] font-semibold">Priority Strategy Call</span>
              <h3 className="text-lg sm:text-xl font-bold text-white">Book a Technical Consultation</h3>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-[#8FA0B8] hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close consultation modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {submitted ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-500/20 text-[#00C853] rounded-full flex items-center justify-center border border-green-500/40">
                <CheckCircle2 size={36} />
              </div>
              <h4 className="text-2xl font-bold text-white">Consultation Request Confirmed</h4>
              <p className="text-[#D5DFEC] max-w-md mx-auto text-sm leading-relaxed">
                Thank you, <strong className="text-white">{formData.name}</strong>. Our senior technical architects have received your consultation brief. We will reach out at <strong className="text-[#159DE8]">{formData.email}</strong> within 24 business hours to lock in your preferred session.
              </p>
              <div className="p-4 bg-[#0B2342]/50 border border-[#1b2f4a] rounded-xl max-w-md mx-auto text-left text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Selected Domain:</span>
                  <span className="text-white font-medium">{formData.service}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Preferred Time Window:</span>
                  <span className="text-white font-medium">{formData.preferredTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Direct Phone Line:</span>
                  <span className="text-white font-medium">{companyInfo.contact.phone}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="mt-6 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-semibold text-sm hover:shadow-glow-orange transition-all"
              >
                Close & Return to Website
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. John Anderson"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Business Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Company / Organization (Optional)</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Acme Corp"
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Service Required *</label>
                  <select
                    value={formData.service}
                    onChange={e => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  >
                    {formData.service && !servicesData.some(s => s.title === formData.service) && formData.service !== 'General Technical Advisory' && (
                      <option value={formData.service}>{formData.service}</option>
                    )}
                    {servicesData.map(s => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Digital Marketing - Starter Plan ($199/mo)">Digital Marketing - Starter Plan ($199/mo)</option>
                    <option value="Digital Marketing - Growth Plan ($449/mo)">Digital Marketing - Growth Plan ($449/mo)</option>
                    <option value="Digital Marketing - E-Commerce Plan ($799/mo)">Digital Marketing - E-Commerce Plan ($799/mo)</option>
                    <option value="General Technical Advisory">General Technical Advisory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Estimated Budget (Optional)</label>
                  <select
                    value={formData.budget}
                    onChange={e => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-base sm:text-sm"
                  >
                    <option value="Under $5,000">Under $5,000</option>
                    <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                    <option value="$15,000 - $40,000">$15,000 - $40,000</option>
                    <option value="$40,000+">$40,000+ (Enterprise)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Preferred Time Window</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {['Morning (10 AM - 1 PM IST)', 'Afternoon (2 PM - 5 PM IST)', 'Evening (6 PM - 9 PM IST)'].map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setFormData({ ...formData, preferredTime: slot })}
                      className={`text-xs p-2.5 rounded-lg border text-left transition-all ${
                        formData.preferredTime === slot 
                          ? 'bg-[#159DE8]/10 border-[#159DE8] text-white font-medium' 
                          : 'bg-[#050A13] border-[#1b2f4a] text-[#8FA0B8] hover:text-white'
                      }`}
                    >
                      <Clock size={12} className="inline mr-1 text-[#159DE8]" />
                      {slot.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Project Brief or Core Objectives</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Share a brief overview of what you're looking to build or transform..."
                  className="w-full px-3.5 py-2 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/50 focus:outline-none focus:border-[#159DE8] text-base sm:text-sm resize-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-1 text-xs text-[#8FA0B8]">
                <ShieldCheck size={16} className="text-[#00C853] shrink-0" />
                <span>Non-Disclosure Agreement (NDA) automatically protected. We never share your data.</span>
              </div>

              <div className="pt-3 border-t border-[#1b2f4a] flex items-center justify-between">
                <div className="hidden sm:flex items-center gap-2 text-xs text-[#8FA0B8]">
                  <Phone size={14} className="text-[#FF851B]" />
                  <span>Call directly: {companyInfo.contact.phone}</span>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-semibold text-sm hover:shadow-glow-orange transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Scheduling...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Consultation Request</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
